import React from 'react';
import { Box, Typography, LinearProgress, Alert, AlertTitle, Chip } from '@mui/material';

export interface UploadProgressSummaryProps {
  uploadState: 'idle' | 'uploading' | 'complete';
  progress: {
    completed: number;
    failed: number;
    total: number;
  };
}

const UploadProgressSummary: React.FC<UploadProgressSummaryProps> = ({
  uploadState,
  progress,
}) => {
  if (uploadState === 'idle') {
    return null;
  }

  if (uploadState === 'uploading') {
    const percentComplete = progress.total > 0
      ? Math.round((progress.completed / progress.total) * 100)
      : 0;

    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Uploading {progress.completed} of {progress.total}...
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percentComplete}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>
    );
  }

  // uploadState === 'complete'
  const successCount = progress.completed - progress.failed;
  const allSucceeded = progress.failed === 0;

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Alert
        severity={allSucceeded ? 'success' : 'warning'}
        sx={{ mb: 1 }}
      >
        <AlertTitle>{allSucceeded ? 'Upload Complete' : 'Upload Complete with Errors'}</AlertTitle>
        {allSucceeded
          ? `All ${progress.total} outfits uploaded successfully`
          : `${successCount} of ${progress.total} outfits uploaded. ${progress.failed} failed.`}
      </Alert>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Chip
          label={`${successCount} Succeeded`}
          color="success"
          size="small"
        />
        {progress.failed > 0 && (
          <Chip
            label={`${progress.failed} Failed`}
            color="error"
            size="small"
          />
        )}
      </Box>
    </Box>
  );
};

export default UploadProgressSummary;
